import { runBotBatch } from '../../../../../shared/services/bot-bulk-actions';
import { NgTemplateOutlet } from '@angular/common';
import { Component, output, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import { AdminService } from '@shared-angular/services/admin.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ChatbotConfig } from '@shared/index';
import dayjs from 'dayjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Button, ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Popover } from "primeng/popover";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { ProjectPagehearderComponent } from "src/app/project-pagehearder/project-pagehearder.component";
import { AdminPositionBubbleComponent } from "src/app/shared/components/ai-bot/utils/admin-position-bubble/admin-position-bubble.component";
import { BotIconComponent } from "src/app/shared/components/bot-icon/bot-icon.component";
import { BotService } from 'src/app/shared/services/bot.service';
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { MoveBotToProjectComponent } from '../projects-page/project-bots-tab/move-bot-to-project/move-bot-to-project.component';

@Component({
  imports: [RouterLink, NgTemplateOutlet,
    ProjectPagehearderComponent,
    IconFieldModule,
    InputIconModule,
    TableModule,
    BotIconComponent,
    Popover,
    ButtonModule,
    HintComponent,
    AdminPositionBubbleComponent,
    ConfirmDialogModule,
    InputTextModule,
    TagModule,
    BulkActionBarComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './trash-page.component.html',
  styleUrl: './trash-page.component.scss'
})
export class TrashPageComponent {
  // Limits parallel server-side teardowns (files, docs, scrapes) per bot.
  private static readonly BULK_CONCURRENCY = 5;

  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'restore',
        label: $localize`:@@restore:Wiederherstellen`,
        icon: 'ri-arrow-go-back-line',
        disabled: this.bulkBusy,
        command: () => this.runBulk('restore')
      },
      {
        id: 'delete-permanently',
        label: $localize`:@@delete-permanently:Endgültig löschen`,
        icon: 'ri-delete-bin-6-line',
        disabled: this.bulkBusy,
        command: () => this.confirmBulkDelete()
      }
    ];
  }

  public readonly deleteTrashTrigger = output();
  public readonly reloadTrigger = output();

  private readonly _selectedBots = signal<ChatbotConfig[]>([]);
  public get selectedBots(): ChatbotConfig[] { return this._selectedBots(); }
  public set selectedBots(value: ChatbotConfig[]) { this._selectedBots.set(value); }
  private readonly _bulkBusy = signal(false);
  public get bulkBusy(): boolean { return this._bulkBusy(); }
  public set bulkBusy(value: boolean) { this._bulkBusy.set(value); }
  private readonly _bulkFailedNames = signal('');
  public get bulkFailedNames(): string { return this._bulkFailedNames(); }
  public set bulkFailedNames(value: string) { this._bulkFailedNames.set(value); }
  private readonly _bots = signal<ChatbotConfig[]>([]);
  public get bots(): ChatbotConfig[] { return this._bots(); }
  public set bots(value: ChatbotConfig[]) { this._bots.set(value); }
  public contextMenu: (MenuItem & { command: () => void })[] = [];
  private activePop: Popover | null = null;
  public dayjs = dayjs;

  constructor(
    public aRoute: ActivatedRoute,
    public botService: BotService,
    private adminService: AdminService,
    private loginservice: LoginService,
    private modalService: BsModalService,
    private confirmationService: ConfirmationService
  ) {
    this.aRoute.data.subscribe((data) => {
      this.bots = (data as unknown as { bots: ChatbotConfig[] }).bots;
    });
  }

  public async runBulk(action: 'restore' | 'delete', bots = [...this.selectedBots]): Promise<void> {
    if (this.bulkBusy || !bots.length) return;
    this.bulkBusy = true;
    this.bulkFailedNames = '';
    try {
      const result = await runBotBatch(bots, id => action === 'restore' ? this.adminService.untrashBot(id) : this.adminService.deleteBot(id), TrashPageComponent.BULK_CONCURRENCY);
      this.bots = this.bots.filter(bot => !result.succeeded.has(bot._id));
      this.selectedBots = result.failed;
      this.bulkFailedNames = result.failed.map(bot => bot.name).join(', ');
    } finally {
      this.bulkBusy = false;
    }
  }

  public confirmBulkDelete(): void {
    if (this.bulkBusy || !this.selectedBots.length) return;
    const selected = [...this.selectedBots];
    const count = selected.length;
    this.confirmationService.confirm({
      header: $localize`:@@confirm-delete-header:Endgültig löschen?`,
      message: $localize`:@@bulk-permanent-delete-confirm:Möchtest du ${count}:count: ausgewählte Bot(s) unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: { label: $localize`:@@delete-reject:Abbrechen`, severity: 'secondary', outlined: true },
      acceptButtonProps: { label: $localize`:@@delete-accept:Löschen`, severity: 'danger' },
      accept: () => { void this.runBulk('delete', selected); }
    });
  }

  public async reloadBots() {
    this.bots = await this.adminService.loadTrashBots();
    this.selectedBots = this.selectedBots.filter(selected => this.bots.some(bot => bot._id === selected._id));
  }

  public async deleteBot(id: string) {
    await this.adminService.deleteBot(id);
    this.bots = this.bots.filter(bot => bot._id !== id);
    this.selectedBots = this.selectedBots.filter(bot => bot._id !== id);
  }

  public openMenu(event: MouseEvent, popover: Popover, bot: ChatbotConfig, button: Button) {
    if (this.activePop && this.activePop !== popover) {
      this.activePop.hide();
    }
    this.contextMenu = this.getMenuItems(bot);
    popover.toggle(event, button.el.nativeElement);
    this.activePop = popover;
  }

  private getMenuItems(bot: ChatbotConfig) {
    const menu: (MenuItem & { command: () => void })[] = [];
    menu.push({
      label: $localize`:@@move-project:Verschieben`,
      icon: 'ri ri-folder-transfer-line',
      command: () => {
        this.moveBotToProject(bot);
      }
    });
    menu.push({
      label: $localize`:@@restore:Wiederherstellen`,
      icon: 'ri ri-delete-bin-line',
      command: async () => {
        await this.untrashBot(bot._id);
      }
    });
    menu.push({
      label: $localize`:@@delete-permanently:Endgültig löschen`,
      icon: 'ri ri-delete-bin-6-line',
      command: () => {
        this.confirmationCheck(bot._id, false);
      }
    });
    return menu;
  }
  public async untrashBot(id: string) {
    await this.adminService.untrashBot(id);
    this.bots = this.bots.filter(bot => bot._id !== id);
    this.selectedBots = this.selectedBots.filter(bot => bot._id !== id);
    this.reloadTrigger.emit();
  }

  public moveBotToProject(bot: ChatbotConfig) {
    const projectId = bot.projectId;

    this.modalService.show(MoveBotToProjectComponent, { initialState: { bot, projectId: projectId ?? "-" } });
  }

  public confirmationCheck(id: string, deleteBin: boolean) {
    const delAll: string = $localize`:@@delete-all:Möchtest du wirklich alle Inhalte des Papierkorbs unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`;
    const delSingle: string = $localize`:@@delete-single:Möchtest du diesen Bot wirklich unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`;
    this.confirmationService.confirm({
      message: deleteBin ? delAll : delSingle,
      header: $localize`:@@confirm-delete-header:Endgültig löschen?`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        if (!deleteBin) {
          await this.deleteBot(id);
        } else {
          const trashedBots = this.bots.filter(bot => bot.thisIsTrash && bot.creator === this.loginservice.loggedInUser?._id);
          await this.runBulk('delete', trashedBots);
        }
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

}
